// src/pages/Expenses.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useLookups } from '../context/LookupContext';
import { useAppContext } from '../context/AppContext';
import { deleteExpenseRecord, fetchExpensesData, saveOrUpdateExpenseRecord } from '../services/expenseServiece';
import { fetchDependents } from '../services/assetService';
import { LookupValue } from '../services/lookupService';
import { RefreshCw } from 'lucide-react';

// Import your new components
import Notification from '../components/shared/Notification';
import ExpenseHeader from '../components/expenses/ExpenseHeader';
import ExpenseForm from '../components/expenses/ExpenseForm';
import ActionBar from '../components/expenses/ActionBar';
import ExpensesTable from '../components/expenses/ExpensesTable';
import ExpensesMobileView from '../components/expenses/ExpensesMobileView';
import Pagination from '../components/shared/Pagination';
import EmptyState from '../components/expenses/EmptyState';

// Export interfaces to be shared with child components
export interface ExpenseData {
  id: string;
  expenseOwnerType: string;
  expenseType: string;
  expenseSubType: string;
  expenseName: string;
  expenseAmount: string;
  expenseFrequency: string;
  totalExpenseAmount: string;
  totalFamilyExpenseAmount: string;
  status: string;
  familyMemberId?: string;
}

export type ColumnKey = keyof ExpenseData | 'ownerTypeLabel' | 'typeLabel' | 'subTypeLabel' | 'frequencyLabel';

// Constants remain the same
const visibleColumns: ColumnKey[] = ['expenseOwnerType', 'expenseName', 'expenseType', 'expenseSubType', 'expenseAmount', 'expenseFrequency', 'totalExpenseAmount'];

const getColumnDisplayName = (col: ColumnKey): string => {
  const displayNames: Record<ColumnKey, string> = {
    id: 'ID', expenseOwnerType: 'Owner Type', expenseType: 'Type', expenseSubType: 'Sub Type',
    expenseName: 'Name', expenseAmount: 'Amount', expenseFrequency: 'Frequency',
    totalExpenseAmount: 'Total Annual Expense', totalFamilyExpenseAmount: 'Family Annual Expense',
    status: 'Status', familyMemberId: '', ownerTypeLabel: 'Owner Type', typeLabel: 'Type',
    subTypeLabel: 'Sub Type', frequencyLabel: 'Frequency',
  };
  return displayNames[col];
};

const Expenses: React.FC = () => {
    const { state } = useAppContext();
    const { getOptions, getLabel, isLoading: isLookupsLoading } = useLookups();

    // All state definitions are complete
    const [transformedData, setTransformedData] = useState<ExpenseData[]>([]);
    const [dependents, setDependents] = useState<{ id: string; name: string }[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [apiLoading, setApiLoading] = useState(true);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    const [formData, setFormData] = useState<ExpenseData>({
        id: '', expenseOwnerType: '', expenseType: '', expenseSubType: '', expenseName: '', expenseAmount: '',
        expenseFrequency: '', totalExpenseAmount: '', totalFamilyExpenseAmount: '', status: '', familyMemberId: ''
    });
    const [dropdownOptions, setDropdownOptions] = useState<{
        ownerType: LookupValue[];
        expenseType: LookupValue[];
        expenseSubType: LookupValue[];
        expenseFrequency: LookupValue[];
    }>({ ownerType: [], expenseType: [], expenseSubType: [], expenseFrequency: [] });
    const [loadingSubTypes, setLoadingSubTypes] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' | null }>({ key: 'expenseName', direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ColumnKey, string>>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);

    const lookupGroupMap: Record<string, string> = {
      expenseOwnerType: 'expenseOwnerType', expenseType: 'expenseTypes',
      expenseSubType: 'expenseSubTypes', expenseFrequency: 'expenseFrequencies',
    };

    const formatCurrency = useCallback((value: string | number) => {
        const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
        if (isNaN(num)) return 'N/A';
        const symbol = state.currencySymbol || localStorage.getItem('currencySymbol') || '₹';
        return `${symbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.floor(num))}`;
    }, [state.currencySymbol]);

    const resetForm = () => {
        setFormData({
            id: '', expenseOwnerType: '', expenseType: '', expenseSubType: '', expenseName: '', expenseAmount: '',
            expenseFrequency: '', totalExpenseAmount: '', totalFamilyExpenseAmount: '', status: '', familyMemberId: ''
        });
        setEditingId(null);
    };

    const fetchExpenseFromAPI = useCallback(async () => {
        setApiLoading(true);
        try {
            const apiData = await fetchExpensesData(); // Assuming fetch all for now
            if (!apiData || !Array.isArray(apiData.data)) throw new Error('Invalid API response structure');
            
            const rawExpenses: ExpenseData[] = apiData.data.map((item: any) => ({
                id: item.id || '', status: item.status || 'active', expenseOwnerType: item.expenseOwnerType || '',
                expenseType: item.expenseType || '', expenseSubType: item.expenseSubType || '',
                expenseName: item.expenseName || '', expenseAmount: item.expenseAmount || '',
                expenseFrequency: item.expenseFrequency || '', totalExpenseAmount: item.totalExpenseAmount || '',
                totalFamilyExpenseAmount: item.totalFamilyExpenseAmount || '', familyMemberId: item.familyMemberId || '',
            }));
            setTransformedData(rawExpenses);
        } catch (error) {
            console.error('Failed to fetch expense data:', error);
            setOperationStatus({ show: true, message: 'Failed to fetch expenses. Check console.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExpenseFromAPI();
    }, [fetchExpenseFromAPI]);

    useEffect(() => {
        if (!isLookupsLoading) {
            setDropdownOptions({
                ownerType: getOptions('expenseOwnerType'),
                expenseType: getOptions('expenseTypes'),
                expenseFrequency: getOptions('expenseFrequencies'),
                expenseSubType: [],
            });
        }
    }, [isLookupsLoading, getOptions]);

    useEffect(() => {
        const typeVal = formData.expenseType;
        if (!typeVal) {
            setDropdownOptions(prev => ({ ...prev, expenseSubType: [] }));
            return;
        }
        setLoadingSubTypes(true);
        const allSubTypes = getOptions('expenseSubTypes');
        const filteredSubs = allSubTypes.filter(sub => sub.parentValue === typeVal);
        setDropdownOptions(prev => ({ ...prev, expenseSubType: filteredSubs }));
        setLoadingSubTypes(false);
    }, [formData.expenseType, getOptions]);

    useEffect(() => {
        fetchDependents().then(setDependents).catch(err => console.error(err));
    }, []);

    useEffect(() => {
        if (operationStatus.show) {
            const timer = setTimeout(() => setOperationStatus({ ...operationStatus, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [operationStatus]);
    
    // --- ALL HANDLER LOGIC IS NOW COMPLETE ---

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value, ...(name === 'expenseOwnerType' && { familyMemberId: '' }) }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.expenseOwnerType || !formData.expenseName || !formData.expenseType || !formData.expenseAmount) {
            return alert('Please fill in all required fields marked with *');
        }
        if (formData.expenseOwnerType === '2' && !formData.familyMemberId) {
            return alert('Please select a family member for family expenses.');
        }
        setIsFormSubmitted(true);
        try {
            await saveOrUpdateExpenseRecord(formData);
            setShowForm(false);
            resetForm();
            await fetchExpenseFromAPI();
            setOperationStatus({ show: true, message: `Expense ${editingId ? 'updated' : 'created'} successfully!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save expense.', type: 'error' });
        } finally {
            setIsFormSubmitted(false);
        }
    };

    const handleSort = (key: ColumnKey) => {
        let direction: 'asc' | 'desc' = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });
        const sortedData = [...transformedData].sort((a, b) => {
            const group = lookupGroupMap[key];
            let aValue: string | number = group ? getLabel(group, a[key as keyof ExpenseData] as string) : a[key as keyof ExpenseData] ?? '';
            let bValue: string | number = group ? getLabel(group, b[key as keyof ExpenseData] as string) : b[key as keyof ExpenseData] ?? '';

            if (['expenseAmount', 'totalExpenseAmount'].includes(key)) {
                aValue = parseFloat(aValue as string || '0');
                bValue = parseFloat(bValue as string || '0');
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setTransformedData(sortedData);
    };
    
    const handleEdit = (expense: ExpenseData) => {
        setFormData({ ...expense });
        setEditingId(expense.id);
        setShowForm(true);
    };
    
    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this expense?')) {
            try {
                await deleteExpenseRecord(id);
                fetchExpenseFromAPI();
                setOperationStatus({ show: true, message: 'Expense deleted.', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Failed to delete.', type: 'error' });
            }
        }
    };

    const handleRowSelect = (id: string) => {
        setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
    };
    
    const handleSelectAll = () => {
        if (selectedRows.length === paginatedData.length && paginatedData.length > 0) {
            setSelectedRows([]);
        } else {
            setSelectedRows(paginatedData.map(d => d.id));
        }
    };
    
    const handleBulkDelete = async () => {
        if (window.confirm(`Delete ${selectedRows.length} selected items?`)) {
            try {
                await Promise.all(selectedRows.map(id => deleteExpenseRecord(id)));
                fetchExpenseFromAPI();
                setSelectedRows([]);
                setOperationStatus({ show: true, message: 'Bulk delete successful!', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Bulk delete failed.', type: 'error' });
            }
        }
    };

    const exportToCSV = () => {
        const headers = visibleColumns.map(col => getColumnDisplayName(col));
        const rows = filteredData.map(expense => visibleColumns.map(col => {
            const group = lookupGroupMap[col];
            if (group) return getLabel(group, expense[col as keyof ExpenseData] as string);
            if (['expenseAmount', 'totalExpenseAmount'].includes(col)) return formatCurrency(expense[col as keyof ExpenseData] as string);
            return expense[col as keyof ExpenseData] || 'N/A';
        }));
        const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'expenses_data.csv';
        link.click();
    };

    const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const importedData = JSON.parse(text).expenses as ExpenseData[];
            await Promise.all(importedData.map(exp => saveOrUpdateExpenseRecord(exp)));
            setOperationStatus({ show: true, message: 'Data imported successfully!', type: 'success' });
            fetchExpenseFromAPI();
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to import data.', type: 'error' });
        }
    };

    const filteredData = transformedData.filter(expense => {
        const getDisplayValue = (colKey: keyof ExpenseData) => {
            const group = lookupGroupMap[colKey];
            return group ? getLabel(group, expense[colKey] as string) : expense[colKey] || 'N/A';
        };
        const matchesGlobal = searchTerm ? Object.keys(expense).some(key =>
            String(getDisplayValue(key as keyof ExpenseData)).toLowerCase().includes(searchTerm.toLowerCase())
        ) : true;
        const matchesColumns = Object.entries(columnFilters).every(([key, filterValue]) =>
            !filterValue || String(getDisplayValue(key as keyof ExpenseData)).toLowerCase().includes(String(filterValue).toLowerCase())
        );
        return matchesGlobal && matchesColumns;
    });

    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalExpenses = transformedData.reduce((sum, exp) => sum + parseFloat(exp.totalExpenseAmount || '0'), 0);

    const paginationProps = {
        currentPage, totalPages, totalItems, itemsPerPage, startIndex, endIndex,
        handlePageChange: setCurrentPage,
        handleItemsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
            setItemsPerPage(parseInt(e.target.value));
            setCurrentPage(1);
        },
    };

    const handleAddClick = () => {
        resetForm();
        setShowForm(true);
    };

    const getSortIndicator = (key: ColumnKey) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? '↑' : '↓';
        return '';
    };

    return (
        <div className="space-y-8 p-4 sm:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />

            <ExpenseHeader
                totalExpenses={totalExpenses}
                recordCount={filteredData.length}
                apiLoading={apiLoading}
                onAdd={handleAddClick}
                formatCurrency={formatCurrency}
            />

            {showForm && (
                <ExpenseForm
                    formData={formData}
                    editingId={editingId}
                    isFormSubmitted={isFormSubmitted}
                    dropdownOptions={dropdownOptions}
                    dependents={dependents}
                    loadingSubTypes={loadingSubTypes}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    handleSelectChange={handleSelectChange}
                    onCancel={() => { setShowForm(false); resetForm(); }}
                />
            )}

            <ExpensesMobileView
                paginatedData={paginatedData}
                apiLoading={apiLoading}
                searchTerm={searchTerm}
                onSearchChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                getLabel={getLabel}
                formatCurrency={formatCurrency}
                paginationProps={paginationProps}
            />
             {filteredData.length >0 && (
              <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-x-auto">
                  <ActionBar
                      searchTerm={searchTerm}
                      onSearchChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      // onExport={exportToCSV}
                      // onImport={handleImportJSON}
                      onBulkDelete={handleBulkDelete}
                      selectedRowCount={selectedRows.length}
                  />
                  {apiLoading ? (
                      <div className="text-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-green-600" /></div>
                  ) : (
                      <ExpensesTable
                           paginatedData={paginatedData}
                        visibleColumns={visibleColumns}
                        getColumnDisplayName={getColumnDisplayName}
                        getSortIndicator={getSortIndicator}
                        handleSort={handleSort}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        selectedRows={selectedRows}
                        handleSelectAll={handleSelectAll}
                        handleRowSelect={handleRowSelect}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        formatCurrency={formatCurrency}
                        getLabel={getLabel}
                        lookupGroupMap={lookupGroupMap}
                      />
                  )}
                  <Pagination {...paginationProps} />
              </div>
            )}

            {filteredData.length === 0 && !apiLoading && (
                <EmptyState onAdd={handleAddClick} />
            )}
        </div>
    );
};

export default Expenses;